import { useEffect, useRef, useState } from "react"
import { Box, IconButton, ListItemButton, ListItemText } from "@mui/material"
import { DisplayLegislationItem } from "event-definitions"
import { ListItemBox } from "lib/@components/ListItemBox"
import { IsEditMode } from "lib/@components/edit-mode"
import { prevent } from "lib/prevent"
import { confirm } from "lib/@dialogs/confirm"
import { SortableItem } from "lib/@components/SortableItem"
import { DragHandle } from "lib/dragHandle"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorActions } from "./hooks/use-schedule-editor-actions"
import Iconify from "minimals-template/components/Iconify"
import { LegislationHealthIcon } from "./legislation-health-icon"
import { SCHEDULE_EDITOR_ACTION_RESTORE_DELETED_LEGISLATION } from "./lib/schedule-editor-action-types"

export function DraggableLegislationItem({ item }) {
    const [props, setProps] = useState({})
    const sortId = item

    return (
        <SortableItem id={sortId} setDragProps={setProps}>
            <LegislationItem item={item} extra={<DragHandle {...props} />} />
        </SortableItem>
    )
}

export function LegislationItem({ item, extra = null }) {
    const { target, legislation } = useBoundContext()
    const { notifyDraftStructureChanged, pushUndo } = useScheduleEditorActions()
    item = legislation.find((i) => i.legacyId === String(item)) ?? {}
    const { redirectUrl, title } = item
    const ref = useRef()
    useEffect(raiseDisplayEvent, [item])

    return (
        !!title && (
            <ListItemButton ref={ref} size="small" onClick={() => window.open(redirectUrl, "legislation")}>
                <ListItemBox>
                    {extra}
                    <Box sx={{ mr: 1, color: "primary.main" }}>
                        <Iconify icon="ci:external-link" />
                    </Box>
                    <ListItemText primary={title} />
                    <Box flex={1} />
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            "& span": { display: "inline-flex", alignItems: "center", justifyContent: "center" },
                            "& svg": { display: "block" },
                        }}
                    >
                        <LegislationHealthIcon legislationId={item?.legacyId} />
                    </Box>
                    <IsEditMode>
                        <IconButton
                            aria-label="Remove legislation"
                            onClick={prevent(confirmRemove)}
                            color="secondary"
                            data-cy="remove-legislation-reference"
                            sx={{ width: 40, height: 40, flexShrink: 0, "& svg": { display: "block" } }}
                        >
                            <Iconify icon="mdi:delete" sx={{ fontSize: 22 }} />
                        </IconButton>
                    </IsEditMode>
                </ListItemBox>
            </ListItemButton>
        )
    )

    function raiseDisplayEvent() {
        if (!item?.legacyId) return
        DisplayLegislationItem(item.legacyId).raise(ref.current)
    }

    async function confirmRemove() {
        const ok = await confirm(
            <>
                Are you sure you want to remove this legislation from the schedule?
                <Box component="span" sx={{ display: "block", mt: 1.5, fontWeight: "bold" }}>
                    {title}
                </Box>
            </>,
            "Remove Legislation from Schedule"
        )
        if (!ok) return
        remove()
    }

    function remove() {
        const index = target.legislation.findIndex((current) => String(current) === String(item.legacyId))
        if (index === -1) {
            return
        }
        const removed = target.legislation[index]
        target.legislation.splice(index, 1)

        notifyDraftStructureChanged?.()

        if (pushUndo) {
            pushUndo({ type: SCHEDULE_EDITOR_ACTION_RESTORE_DELETED_LEGISLATION, legislation: removed, index })
        }
    }
}
